import { http } from "src/axios";
import type { WorkPacketDisputesInfo, WorkPacketFilters } from "src/types/work-packets";
import { type ServerModelAttachment } from "src/types/work-packets";
import { computeFilters } from "src/pages/UserDashboard/WorkPackets/api/workPacketsAPI";
import { WorkPacketType } from "src/pages/UserDashboard/WorkPackets/WorkPacketType";
import { encodeUrlParams } from "./encodeUrlParams";
import { ServerCase } from "src/types/cases";
import {
  mapServerEscalationToLocalCase,
  mapServerSettlementToLocalCase,
} from "src/pages/UserDashboard/Cases/mappers/mapServerToLocalCase";
import type { AxiosRequestConfig } from "axios";
import apiClient from "src/pages/UserDashboard/WorkPackets/api/apiClient";
import type {
  CaseCorrespondenceResponse,
  CaseItemsListResponse,
  FetchCaseItemsArgs,
  GetCaseItemsResponse,
  ServerResponse,
} from "./types";
import { CaseType } from "src/pages/UserDashboard/WorkPackets/Cases/case-types";
import FileDownloadServices from "../../../../Services/FileDownloadServices";

import { EMPTY_ARRAY, EMPTY_OBJECT } from "src/utils/empty-values";
import { serverToLocalCaseItemMappers } from "src/pages/UserDashboard/Cases/mappers/mapServerToLocalCaseItem";
import { parseDate } from "src/utils/date-parse";
import { Correspondence } from "src/pages/UserDashboard/WorkPackets/Cases/tabs/CaseCorrespondence/correspondence-types";
import { CorrespondenceWorkFlag } from "src/pages/UserDashboard/WorkPackets/Cases/correspondence-work-flags.ts";
import type { ServerCaseFilters } from "../../WorkPackets/Cases/casesApi";
import { parseMaybeDateString } from "src/utils/parse-maybe-date-string";
import type { ScopeActivationInfo } from "../../WorkPackets/activation-status/activation-status-types";
import { CaseStatus } from "./constants";

const { downloadFile } = FileDownloadServices;

interface CaseCreateResponse {
  CASE_ID: string;
  WORK_PACKET_COUNT: number;
  TOTAL_DISPUTE_AMOUNT: number;
}

export interface CreateCaseResult {
  caseId: string;
  workPacketCount: number;
  totalDisputeAmount: number;
}

export async function createCase({
  caseId,
  caseType,
  caseCreatedAt,
  workPacketIds,
  excludedWorkPacketIds,
  filters,
  workPacketType,
  caseTextTitle,
  caseTextBody,
  processorId,
  backupProcessorId,
  disputeId,
}: {
  caseId: string;
  workPacketIds: string[];
  excludedWorkPacketIds: string[];
  filters: WorkPacketFilters;
  caseType: string;
  caseCreatedAt: string;
  workPacketType: WorkPacketType;
  caseTextTitle: string;
  caseTextBody: string;
  processorId: string | null;
  backupProcessorId: string | null;
  disputeId?: string | null;
}): Promise<CreateCaseResult> {
  const payload = {
    amazon_case_id: caseId,
    work_packet_ids: workPacketIds,
    case_type: caseType,
    case_created_at: caseCreatedAt,
    excluded_work_packets: excludedWorkPacketIds,
    filters: JSON.stringify(computeFilters(filters, workPacketType)),
    case_text_title: caseTextTitle,
    case_text_body: caseTextBody,
    processor_id: processorId,
    backup_processor_id: backupProcessorId,
    dispute_id: disputeId ?? null,
  };
  const response = await http.post<{ data: CaseCreateResponse }>(`/api/v2/cases`, payload, {
    errorMessage: "Error while creating case",
  });
  const data = response.data.data;
  return {
    caseId: data.CASE_ID,
    workPacketCount: data.WORK_PACKET_COUNT,
    totalDisputeAmount: data.TOTAL_DISPUTE_AMOUNT,
  };
}

export async function getDisputesIdsAndVendorInfo(
  {
    workPacketType,
    workPacketIds,
    excludedWorkPackets,
    filters,
  }: {
    workPacketType: WorkPacketType;
    workPacketIds: string[];
    excludedWorkPackets: string[];
    filters: WorkPacketFilters;
  },
  signal?: AbortSignal,
): Promise<WorkPacketDisputesInfo> {
  if (workPacketType == WorkPacketType.CHARGEBACKS) {
    const params = encodeUrlParams({
      work_packet_ids: workPacketIds,
      excluded_work_packets: excludedWorkPackets,
      filters: JSON.stringify(computeFilters(filters, workPacketType)),
    });
    const response = await http.get(`api/v2/work_packets/chargebacks/pre_create_case`, { params, signal });
    return response.data.data as WorkPacketDisputesInfo;
  } else {
    const params = encodeUrlParams({
      work_packet_ids: workPacketIds,
      excluded_work_packets: excludedWorkPackets,
      filters: JSON.stringify(computeFilters(filters, workPacketType)),
    });
    const response = await http.get(`api/v2/work_packets/shortages/pre_create_case`, { params, signal });
    return response.data.data as WorkPacketDisputesInfo;
  }
}

interface CaseResponse {
  data: {
    cases: ServerCase[];
  };
}

export async function updateCase(caseId: string, caseType: CaseType, updateParams: Record<string, any>) {
  const caseUpdateUrl = {
    [CaseType.CHARGEBACK_ESCALATION]: "/api/v2/cases/chargebacks",
    [CaseType.SHORTAGE_SETTLEMENT]: "/api/v2/cases/shortages",
    [CaseType.SHORTAGE_ESCALATION]: "/api/v2/cases/shortages",
  };
  const response = await http.put<CaseResponse>(
    caseUpdateUrl[caseType],
    {
      case_id: caseId,
      update_params: updateParams,
    },
    { errorMessage: "Error while updating case" },
  );
  return response.data;
}

export interface BulkUpdateCasesParams {
  filters: ServerCaseFilters;
  selectedItems: string[];
  excludedItems: string[];
  exclusionBased: boolean;
  caseType: string;
  updateParams: BulkUpdateCasesParamsInner;
}

export interface BulkUpdateCasesParamsInner {
  CASE_STATUS?: CaseStatus;
  CASE_OWNER_ID?: string;
  BACKUP_CASE_OWNER_ID?: string;
  PROCESSOR_ID?: string;
  BACKUP_PROCESSOR_ID?: string;
}

export async function bulkUpdateCases({
  filters,
  selectedItems,
  excludedItems,
  exclusionBased,
  caseType,
  updateParams,
}: {
  filters: ServerCaseFilters;
  selectedItems: string[];
  excludedItems: string[];
  exclusionBased: boolean;
  caseType: string;
  updateParams: Record<string, any>;
}) {
  const response = await http.put(
    "/api/v2/cases/bulk_update",
    {
      filters: Object.fromEntries(Object.entries(filters).filter(([_key, val]) => val.length > 0)),
      case_type: caseType,
      selected_items: exclusionBased ? [] : selectedItems,
      excluded_items: exclusionBased ? excludedItems : [],
      update_params: updateParams,
    },
    { errorMessage: "Error while updating cases" },
  );
  return response.data;
}

export async function deleteCaseCorrespondenceWorkFlag(caseId: string, flag: CorrespondenceWorkFlag) {
  const response = await http.delete(`/api/v2/cases/${caseId}/correspondence_work_flags/${flag}`, {
    errorMessage: "Error while removing work flag",
  });
  return response.data;
}

export async function bulkUpdateCaseItems({
  caseId,
  excludedItems,
  selectedItems,
  allSelected,
  updateParams,
}: {
  selectedItems: string[];
  excludedItems: string[];
  allSelected: boolean;
  caseId: string;
  caseType: string;
  updateParams: Record<string, any>;
}) {
  const response = await http.put<CaseResponse>(
    "/api/v2/cases/case_items",
    {
      case_id: caseId,
      selected_items: selectedItems,
      excluded_items: excludedItems,
      all_items_selected: allSelected,
      update_params: updateParams,
    },
    { errorMessage: "Error while updating case" },
  );
  return response.data;
}

export async function getSettlementCaseById(caseId: string, caseType: CaseType) {
  const response = await http.get<CaseResponse>("/api/v2/cases/" + caseId, { params: { case_type: caseType } });
  return mapServerSettlementToLocalCase(response.data.data.cases[0]);
}

export async function getEscalationCaseById(caseId: string, caseType: CaseType) {
  const response = await http.get<CaseResponse>("/api/v2/cases/" + caseId, { params: { case_type: caseType } });
  return mapServerEscalationToLocalCase(response.data.data.cases[0]);
}

export async function getCaseStoreScopeInfo(caseId: string, caseType: CaseType): Promise<ScopeActivationInfo> {
  const response = await http.get(`/api/v2/cases/${caseId}/store_scope_info`, {
    params: { case_type: caseType },
    errorMessage: "Error while fetching store scope info",
  });

  return {
    isStoreActive: response.data.data.IS_STORE_ACTIVE,
    deactivationDate: parseMaybeDateString(response.data.data.SCOPE_DEACTIVATION_DATE) ?? undefined,
  };
}

export async function getCaseAttachments(caseId: string): Promise<ServerModelAttachment[]> {
  const response = await http.get("/api/v2/cases/" + caseId + "/attachments");
  return response.data.model_attachments;
}

export async function getCaseInvoices(caseId: string): Promise<ServerModelAttachment[]> {
  const response = await http.get("/api/v2/cases/" + caseId + "/attachments");
  return response.data.model_attachments;
}

export async function getCaseCorrespondenceWorkFlagById(caseId: string): Promise<CorrespondenceWorkFlag[]> {
  const response = await http.get<{ data: string[] }>("/api/v2/cases/" + caseId + "/correspondence_work_flags");
  const flags = (response.data.data || []).filter((flag): flag is CorrespondenceWorkFlag =>
    Object.values(CorrespondenceWorkFlag).includes(flag as CorrespondenceWorkFlag),
  );
  return flags;
}

export async function uploadCaseAttachment(
  caseId: string,
  file: File,
  onUploadProgress?: AxiosRequestConfig["onUploadProgress"],
): Promise<any> {
  const formData = new FormData();
  formData.set("file", file);
  formData.set("file_name", file.name);
  formData.set("model", "cases");
  formData.set("model_id", caseId);

  return apiClient.post(`/api/v2/attachments`, formData, {
    adapter: "xhr",
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress,
  });
}

export async function getCaseItems(
  caseId: string,
  caseType: CaseType,
  { page, pageSize, sorting, filters }: FetchCaseItemsArgs,
  signal: AbortSignal,
): Promise<GetCaseItemsResponse> {
  const response = await http.get<ServerResponse<CaseItemsListResponse>>(`/api/v2/cases/${caseId}/case_items`, {
    signal,
    params: {
      page: page,
      per_page: pageSize,
      sorting: JSON.stringify(sorting ? { [sorting.key]: sorting.direction } : EMPTY_OBJECT),
      filters: JSON.stringify(filters ?? EMPTY_OBJECT),
    },
    errorMessage: "Error while fetching case items",
  });

  return {
    items: response.data.data.items.map(serverToLocalCaseItemMappers[caseType]),
    total_records: response.data.data.total_records,
    pages: response.data.data.pages,
  };
}

export async function exportCaseItems(caseId: string): Promise<void> {
  const response = await http.get<ServerResponse<CaseItemsListResponse>>(`/api/v2/cases/${caseId}/case_items/export`, {
    responseType: "blob",
  });

  downloadFile(response, "workpackets_in_case_item", "csv");
}

export async function deleteCaseItem(caseId: string, caseItemId: string, caseType: CaseType): Promise<void> {
  const response = await http.delete(`/api/v2/cases/${caseId}/case_items/${caseItemId}`, {
    data: { case_type: caseType },
  });
  return response.data;
}

export async function bulkDeleteCaseItems({
  caseId,
  caseType,
  excludedItems,
  selectedItems,
  allSelected,
}: {
  selectedItems: string[];
  excludedItems: string[];
  allSelected: boolean;
  caseId: string;
  caseType: CaseType;
}) {
  const response = await http.post(
    `/api/v2/cases/${caseId}/case_items/bulk_delete`,
    {
      case_type: caseType,
      selected_items: selectedItems,
      excluded_items: excludedItems,
      all_items_selected: allSelected,
    },
    { errorMessage: "Error while deleting work packets" },
  );
  return response.data;
}

export async function fetchCaseCorrespondence(caseId: string): Promise<Correspondence | null> {
  const response = await http.get<CaseCorrespondenceResponse>(`/api/v2/cases/${caseId}/case_correspondence`);

  const correspondence = response.data.case_correspondence;

  if (correspondence === null) return null;

  return {
    amazonCaseId: correspondence.AMAZON_CASE_ID,
    primaryEmail: correspondence.PRIMARY_EMAIL || "",
    dateOfLastMessage: correspondence.CORRESPONDENCE_MESSAGES[0]?.SENT_AT || "",
    ourLastMessage: correspondence.CORRESPONDENCE_MESSAGES.find(m => m.SENDER !== "Amazon")?.SENT_AT || "",
    status: correspondence.STATUS || "",
    vcLink: correspondence.VC_LINK || "",
    messages: correspondence.CORRESPONDENCE_MESSAGES.map(message => ({
      id: message.CGID,
      from: message.SENDER || "",
      timestamp: parseDate(message.SENT_AT),
      content: message.BODY,
    })),
  };
}

export interface BatchUpdateCaseItemParams {
  caseItemId: string;
  remainingBalanceOpen?: number;
}

export interface BatchUpdateCaseItemsParams {
  caseId: string;
  items: BatchUpdateCaseItemParams[];
}

export async function batchUpdateCaseItems({ caseId, items }: BatchUpdateCaseItemsParams) {
  const response = await http.patch(
    `/api/v2/cases/${caseId}/case_items`,
    {
      caseItems: items || EMPTY_ARRAY,
    },
    { errorMessage: "Error while updating case items" },
  );
  return response.data;
}
